import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { playNewOrder, playNotification, playCritical } from '../lib/sound';
import { initialState, LS_KEY, soundEnabled } from './state';
import { useCartActions } from './actions/cartActions';
import { useOrderActions } from './actions/orderActions';
import { useFinanceActions } from './actions/financeActions';
import { useAdminActions } from './actions/adminActions';
import { useEngagementActions } from './actions/engagementActions';
import { useWalletActions } from './actions/walletActions';
import { useVoucherActions } from './actions/voucherActions';
import { useAuth } from './AuthContext';
import api from '../lib/api';
import { fetchPublic, fetchRole, mapNotification } from './loadServerData';

const AppContext = createContext(null);

export { soundEnabled, setSoundEnabled, soundPreferenceKnown, referralCodeFor } from './state';

export function AppProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(initialState);
  const stateRef = useRef(state);
  const userRef = useRef(user);
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { userRef.current = user; }, [user]);
  const getState = useCallback(() => stateRef.current, []);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  }, [state]);

  const refresh = useCallback(async (includePublic = false) => {
    try {
      const patch = {};
      if (includePublic) Object.assign(patch, await fetchPublic());
      if (userRef.current) Object.assign(patch, await fetchRole(userRef.current));
      setState((s) => ({ ...s, ...patch }));
    } catch (e) { /* keep last known state on network errors */ }
  }, []);

  useEffect(() => { refresh(true); }, [user, refresh]);

  const knownNotifs = useRef(new Set());
  const notifInit = useRef(false);
  useEffect(() => {
    if (!user) { notifInit.current = false; knownNotifs.current = new Set(); return undefined; }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await api.get('/notifications');
        if (cancelled) return;
        const notifs = res.data.map(mapNotification);
        const fresh = notifs.filter((n) => !knownNotifs.current.has(n.id));
        fresh.forEach((n) => knownNotifs.current.add(n.id));
        setState((s) => ({ ...s, notifications: notifs }));
        if (notifInit.current && soundEnabled()) {
          fresh.filter((n) => !n.isRead && ['HIGH', 'CRITICAL'].includes(n.priority)).forEach((n) => {
            if (n.priority === 'CRITICAL') { playCritical(0.9); toast.error(n.title, { description: n.message }); }
            else {
              if (n.type === 'NEW_ORDER') playNewOrder(0.6); else playNotification(0.6);
              toast(n.title, { description: n.message });
            }
          });
        }
        notifInit.current = true;
      } catch (e) { /* offline */ }
    };
    poll();
    const t = setInterval(poll, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  const pushNotification = useCallback((n) => {
    const full = { id: `local${Date.now()}`, isRead: false, createdAt: new Date().toISOString(), ...n };
    setState((s) => ({ ...s, notifications: [full, ...s.notifications] }));
    if (n.priority === 'CRITICAL') toast.error(n.title, { description: n.message });
    else if (n.priority === 'HIGH') toast(n.title, { description: n.message });
    return full;
  }, []);

  const addAudit = useCallback((admin, action, entity, prev, next) => {
    setState((s) => ({
      ...s,
      auditLog: [{ id: `a${Date.now()}`, admin, action, entity, prev, next, at: new Date().toISOString(), ip: 'internal' }, ...s.auditLog],
    }));
  }, []);

  const cart = useCartActions(setState);
  const orders = useOrderActions({ state, setState, refresh });
  const finance = useFinanceActions({ refresh });
  const admin = useAdminActions({ setState, refresh });
  const engage = useEngagementActions({ setState, refresh });
  const wallet = useWalletActions({ refresh });
  const voucher = useVoucherActions({ refresh });

  const unreadCount = useCallback((recipientType) =>
    state.notifications.filter((n) => n.recipientType === recipientType && !n.isRead).length,
  [state.notifications]);

  const markRead = useCallback((id) => {
    setState((s) => ({ ...s, notifications: s.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)) }));
    api.post('/notifications/mark-read', { ids: [id] }).catch(() => {});
  }, []);

  const markAllRead = useCallback(() => {
    setState((s) => ({ ...s, notifications: s.notifications.map((n) => ({ ...n, isRead: true })) }));
    api.post('/notifications/mark-read', {}).catch(() => {});
  }, []);

  const setMerchantNotif = useCallback((patch) => setState((s) => ({ ...s, merchantNotif: { ...s.merchantNotif, ...patch } })), []);
  const setAdminNotif = useCallback((patch) => setState((s) => ({ ...s, adminNotif: { ...s.adminNotif, ...patch } })), []);

  const merchantById = useCallback((id) => state.customMerchants.find((m) => m.id === id) || null, [state.customMerchants]);
  const productsByMerchant = useCallback((id) => state.customProducts.filter((p) => p.merchantId === id), [state.customProducts]);

  const value = useMemo(() => ({
    ...state,
    pushNotification, getState, addAudit,
    ...cart, ...orders, ...finance, ...admin, ...engage, ...wallet, ...voucher,
    unreadCount, markRead, markAllRead, setMerchantNotif, setAdminNotif, merchantById, productsByMerchant, refresh,
  }), [state, pushNotification, getState, addAudit, cart, orders, finance, admin, engage, wallet, voucher, unreadCount, markRead, markAllRead, setMerchantNotif, setAdminNotif, merchantById, productsByMerchant, refresh]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  return useContext(AppContext);
}
